import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Check, ArrowRight, AlertCircle, Calendar, CreditCard, Users } from 'lucide-react';
import { getPlanById } from '../../services/storage';
import { Link } from 'react-router-dom';

export const ClientSubscription = () => {
  const { subscription } = useAuth();
  
  const plan = subscription ? getPlanById(subscription.planId) : null;

  if (!subscription || !plan) {
    return (
      <DashboardLayout>
        <Helmet>
          <title>Minha Assinatura | Portal Ethics</title>
        </Helmet>
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-neutral-900 mb-2">Nenhuma assinatura ativa</h2>
          <p className="text-neutral-600 mb-6">Você ainda não possui uma assinatura ativa.</p>
          <Link to="/planos">
            <Button variant="primary">Ver Planos</Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Helmet>
        <title>Minha Assinatura | Portal Ethics</title>
      </Helmet>

      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Minha Assinatura</h1>
          <p className="text-neutral-600">Gerencie sua assinatura e plano atual</p>
        </div>

        {/* Current Plan */}
        <Card className="p-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-2xl font-bold text-neutral-900">Plano {plan.name}</h2>
                <Badge variant="success">Ativo</Badge>
              </div>
              <p className="text-neutral-600">{plan.description}</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-neutral-900">
                {new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).format(subscription.amount)}
              </p>
              <p className="text-neutral-500">
                /{subscription.billingCycle === 'monthly' ? 'mês' : 'ano'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-lg">
              <Calendar className="w-5 h-5 text-primary-blue" />
              <div>
                <p className="text-sm text-neutral-600">Início</p>
                <p className="font-medium text-neutral-900">
                  {new Date(subscription.startDate).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-lg">
              <CreditCard className="w-5 h-5 text-primary-blue" />
              <div>
                <p className="text-sm text-neutral-600">Próxima Cobrança</p>
                <p className="font-medium text-neutral-900">
                  {new Date(subscription.nextBillingDate).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-lg">
              <Users className="w-5 h-5 text-primary-blue" />
              <div>
                <p className="text-sm text-neutral-600">Limite de Usuários</p>
                <p className="font-medium text-neutral-900">
                  {plan.maxUsers === 999999 ? 'Ilimitados' : plan.maxUsers}
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-neutral-200 pt-6">
            <h3 className="font-semibold text-neutral-900 mb-4">Recursos Incluídos</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {plan.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <span className="text-neutral-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="p-6">
            <h3 className="font-semibold text-neutral-900 mb-2">Quer mais recursos?</h3>
            <p className="text-sm text-neutral-600 mb-4">
              Faça upgrade do seu plano para acessar mais funcionalidades.
            </p>
            <Link to="/planos">
              <Button variant="primary" className="w-full flex items-center justify-center gap-2">
                Fazer Upgrade
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold text-neutral-900 mb-2">Precisa de ajuda?</h3>
            <p className="text-sm text-neutral-600 mb-4">
              Entre em contato com nosso suporte para alterar sua assinatura.
            </p>
            <Link to="/dashboard/suporte">
              <Button variant="outline" className="w-full">
                Falar com Suporte
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};
